import { useMemo } from "react";
import { Link } from "react-router-dom";
import { client } from "../../../hooks/Client";
import Table from "../../common/table/Table.jsx";
import TableCell from "../../common/table/TableCell.jsx";
import buildPresenceYearRows from "../../../helpers/personas/buildPresenceYearRows";
import useStatusDisplays from '../../../hooks/useStatusDisplay'

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
//
// Het jaar in één oogopslag: deelnemers als rijen, twaalf maanden als kolommen.
// De maandweergave (`PresenceTotals`) toont deelnemers × dagen en past daarom
// per maand op het scherm; wie het jaar wil overzien moest twaalf keer wisselen
// en zelf optellen.
//
// Deze weergave laat bewust ook deelnemers zien die niet meer actief zijn: dat
// was de vraag, en het is de enige plek waar een afgesloten traject nog terug
// te zien is. De status staat bij de naam, want een lege tweede jaarhelft
// betekent bij een uitgestroomde deelnemer iets heel anders dan bij een
// actieve.

/** Kolomkoppen; de volgorde is die van `buildPresenceYearRows`. */
const MONTH_LABELS = [
    "Jan", "Feb", "Mrt", "Apr", "Mei", "Jun",
    "Jul", "Aug", "Sep", "Okt", "Nov", "Dec",
];

/** Wat er in een maandcel staat als er niets geregistreerd is. */
const NOTHING_RECORDED = "–";

/**
 * Wat er staat als een deelnemer geen naam heeft. Dat komt voor: er staan half
 * aangemaakte documenten in de database, zonder `Name`. Zonder deze tekst is de
 * rij een lege, aanklikbare regel die je nergens aan kunt koppelen — en hij telt
 * wél mee in de totalen en in de export.
 */
const NO_NAME = "(naam ontbreekt)";

/**
 * Een status die niet in `PERSONA_STATUS_LABELS` staat. Kwam boven bij het
 * testen: twee deelnemers dragen `'planning'`, wat de status van een húíshouden
 * is. De ruwe sleutel tonen zet een intern woord op het scherm van de gebruiker.
 */
const UNKNOWN_STATUS = "Onbekend";

/**
 * Kleurt een maandcel. Een maand met alleen ziek- en afwezigheidsmeldingen is
 * rood, een maand waarin iets is bijgesteld oranje, de rest groen. Zonder
 * registraties blijft de cel kleurloos — daar valt niets over te zeggen.
 *
 * @param {import('../../../helpers/personas/buildPresenceYearRows').PresenceYearMonth} month
 * @returns {string} Klassenaam, of '' als de maand leeg is
 */
const monthCellClass = (month) => {
    if (!month.count) return "";
    if (month.absent === month.count) return "presence-year-table__cell--absent";
    if (month.corrections > 0) return "presence-year-table__cell--correction";
    return "presence-year-table__cell--correct";
};

/**
 * Het enige wat een getal in deze tabel op het scherm zet — cel, rijtotaal,
 * voetregel en groepstotaal gebruiken alle vier deze functie.
 *
 * Dat is geen netheid maar de kern van de zaak: zolang elke plek zijn eigen
 * `count ? … : '–'` heeft, lopen ze bij de volgende wijziging uiteen. Precies zo
 * ontstond de fout die deze functie oplost — de cellen maakten het onderscheid,
 * de voetregel telde alleen uren op en zette een '0.0' neer waar de hele kolom
 * erboven streepjes had.
 *
 * AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero
 *
 * @param {number} hours - Opgetelde uren
 * @param {number} count - Hoeveel rapporten daaraan ten grondslag liggen. Nul
 *   betekent 'er is niets vastgelegd' — dat is iets anders dan nul uur.
 * @returns {string}
 */
const formatCell = (hours, count) => (count ? hours.toFixed(1) : NOTHING_RECORDED);

/**
 * @param {object} props
 * @param {Array<object>} props.personas - Deelnemers die een rij krijgen, in de
 *   volgorde waarin ze getoond worden
 * @param {Array<object>} props.presenceReports - Rapporten uit
 *   `PersonaPresenceReports` over het hele jaar
 * @param {number} props.year - Het jaar waarover geteld wordt
 * @param {boolean} props.loaded - Of de rapporten ÉN de deelnemers binnen zijn.
 *   Zolang dit `false` is wordt er niets beweerd over wat er wel of niet
 *   geregistreerd is — 'leeg' is een bewering, en die is dan onwaar.
 * @param {Error|null} [props.error] - Ging het ophalen mis, dan moet dat te zien
 *   zijn. Anders leest een mislukte read als 'nog bezig' en wacht de gebruiker
 *   op iets dat nooit komt.
 * @returns {JSX.Element}
 */
const PresenceYearTotals = ({ personas, presenceReports, year, loaded, error = null }) => {
    // Alleen de labels: het jaaroverzicht toont de status van een deelnemer als
    // bijschrift, en verbergen hoort een rij nooit te laten verdwijnen.
    const statusLabels = useStatusDisplays().persona.labels


    const rows = useMemo(
        () => buildPresenceYearRows(personas, presenceReports, year),
        [personas, presenceReports, year],
    );

    // Kolomtotalen voor de voetregel: wat stond er in deze maand voor de hele
    // groep? Dat is het getal waar een verantwoording mee begint — en juist
    // daarom telt de voetregel óók de rapporten. Zonder die telling wordt een
    // maand waarin niemand iets registreerde een harde '0.0 uur', wat iets
    // anders beweert dan de streepjes die er in de rijen boven staan. De som
    // klopt dan rekenkundig en liegt alleen in wat hij beweert.
    // AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero
    const columnTotals = useMemo(() => {
        const months = Array.from({ length: MONTH_LABELS.length }, () => ({ hours: 0, count: 0 }));
        let total = 0;
        let count = 0;

        for (const row of rows) {
            row.months.forEach((month, index) => {
                months[index].hours += month.hours;
                months[index].count += month.count;
            });
            total += row.total;
            count += row.count;
        }

        return { months, total, count };
    }, [rows]);

    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    // 'Geen aanwezigheid gevonden' is een bewering aan de lezer, en die is
    // onwaar zolang de gegevens nog onderweg zijn — of nooit aangekomen.
    // Volgorde: eerst de fout, dan 'weet ik nog niet', dan pas de inhoud.
    if (error) {
        return (
            <p className="presence-year-error" role="alert">
                De aanwezigheid kon niet opgehaald worden. Ververs de pagina; blijft het misgaan, meld het dan bij Deccos.
            </p>
        );
    }
    if (!loaded) {
        return (
            <p className="presence-year-loading">Aanwezigheid wordt opgehaald…</p>
        );
    }

    return (
        <div className="presence-year">
            <Table
                outerClassName="presence-totals-table__outer"
                containerClassName="presence-totals-table__container presence-year-table__container"
                tableClassName="presence-totals-table presence-year-table"
                columns={[]}
                headerRows={[
                    [
                        { key: "name", label: "Naam", headerClassName: "common-table__head-cell--sticky-1" },
                        { key: "total", label: "Totaal", headerClassName: "common-table__head-cell--sticky-2" },
                        ...MONTH_LABELS.map((label, index) => ({
                            key: `month-${index}`,
                            label,
                        })),
                    ],
                ]}
                items={rows}
                getItemKey={(row, index) => row.ID || `rij-${index}`}
                renderRow={(row) => (
                    <>
                        <TableCell className="common-table__cell--sticky-1">
                            <Link to={`/${client}/personabuilder/${row.ID}`}>
                                {row.name || NO_NAME}
                            </Link>
                            {row.status && row.status !== "active" && (
                                <span className="presence-year-table__status">
                                    {statusLabels[row.status] || UNKNOWN_STATUS}
                                </span>
                            )}
                        </TableCell>
                        <TableCell className="common-table__cell--sticky-2">
                            {formatCell(row.total, row.count)}
                        </TableCell>
                        {row.months.map((month, index) => (
                            <TableCell
                                key={`${row.ID}-m-${index}`}
                                className={`presence-year-table__month ${monthCellClass(month)}`.trim()}
                            >
                                {formatCell(month.hours, month.count)}
                            </TableCell>
                        ))}
                    </>
                )}
                footerRow={(
                    <>
                        <TableCell className="common-table__cell--sticky-1">Alle deelnemers</TableCell>
                        <TableCell className="common-table__cell--sticky-2">
                            {formatCell(columnTotals.total, columnTotals.count)}
                        </TableCell>
                        {columnTotals.months.map((month, index) => (
                            <TableCell key={`total-m-${index}`}>
                                {formatCell(month.hours, month.count)}
                            </TableCell>
                        ))}
                    </>
                )}
                showSearch={false}
                emptyMessage="Geen aanwezigheid gevonden voor dit jaar."
            />

            <div className="presence-totals-legend">
                <span className="presence-totals-legend__item">
                    <span className="presence-totals-legend__dot presence-totals-legend__dot--correct" />
                    Aanwezig
                </span>
                <span className="presence-totals-legend__item">
                    <span className="presence-totals-legend__dot presence-totals-legend__dot--correction" />
                    Met een aanpassing
                </span>
                <span className="presence-totals-legend__item">
                    <span className="presence-totals-legend__dot presence-totals-legend__dot--absent" />
                    Alleen afwezig
                </span>
                <span className="presence-totals-legend__item">
                    {NOTHING_RECORDED} Niets geregistreerd
                </span>
            </div>
        </div>
    );
};

export default PresenceYearTotals;
export { MONTH_LABELS };
